import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Div, Select } from '@icecreamsocial/components';
import {
	formatISO,
	startOfMonth,
	eachWeekOfInterval,
	eachMonthOfInterval,
	isWithinInterval,
	isBefore,
	subDays,
	eachYearOfInterval,
	startOfWeek,
	format,
	intlFormat,
	isAfter,
	clamp,
} from 'date-fns';

import { useStore } from '../../../../../utils/hooks';
import { statPropMap } from '../../utils';
import { useDashboardFilters } from '../FilterProvider';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../../../../queries/stats';
import { Chart } from '../Chart';
import { Block } from '../Block';
import endOfWeek from 'date-fns/esm/fp/endOfWeek/index.js';

type Stat = {
	date: string;
	totalConversions: number;
	totalInfluencers: number;
	totalInvitesClicked: number;
	totalRevenue: number;
	totalUtilizedInfluencers: number;
	totalUtilizationRate: number;
};

type Interval = {
	start: Date;
	end: Date;
};

type IntervalOpts = 'yearly' | 'monthly' | 'weekly' | 'daily';

/**
 * set the stats to 0
 */
const DEFAULT_STATS = {
	totalRevenue: 0,
	totalConversions: 0,
	totalInvitesClicked: 0,
	totalInfluencers: 0,
	totalUtilizedInfluencers: 0,
	totalUtilizationRate: 0,
};

/**
 * sets the from and to date to today
 */
const defaultDates = (state) => {
	const today = Date.now();
	return {
		fromDate: state.fromDate || formatISO(startOfMonth(today)),
		toDate: state.toDate || formatISO(today),
	};
};

const Stats: React.FC = () => {
	const store = useStore();
	const { state, update } = useDashboardFilters();
	const [interval, setInterval] = useState<IntervalOpts>('daily');
	const defaultState = useMemo(() => defaultDates(state), [state]);

	const { isLoading: isStatsLoading, data: dailyStats } = useQuery(
		queryKeys.getDailyStats({
			affiliateId: store.Orchestrators.Auth.user.affiliateId,
			clientId: store.Orchestrators.Auth.user.clientId || null,
			...defaultState,
			...state,
		}),
		async () => {
			const { items } = await store.getDailyStats({
				affiliateId: store.Orchestrators.Auth.user.affiliateId,
				clientId: store.Orchestrators.Auth.user.clientId || null,
				...defaultState,
				...state,
			});

			const intervalOpt = getInterval(items.length);
			setInterval(intervalOpt);

			const reduced = poolStats(items, intervalOpt);
			const remap = Object.keys(DEFAULT_STATS).reduce(
				(stats, key) => ({
					...stats,
					[key]: reduced.map((datum) => ({
						key,
						value: datum[key],
						date: datum.date,
						description: getIntervalDescription(intervalOpt, {
							date: new Date(datum.date),
							toDate: new Date(new Date(state.toDate)),
						}),
					})),
				}),
				{}
			);
			return remap;
		},
		{ refetchOnWindowFocus: false, retry: 0 }
	);

	const { isLoading: isOverviewLoading, data: dailyOverview } = useQuery(
		queryKeys.getStatsOverview({
			affiliateId: store.Orchestrators.Auth.user.affiliateId,
			clientId: store.Orchestrators.Auth.user.clientId || null,
			...defaultState,
			...state,
		}),
		async () => {
			const { stats } = await store.getDailyStatsOverview({
				affiliateId: store.Orchestrators.Auth.user.affiliateId,
				clientId: store.Orchestrators.Auth.user.clientId || null,
				...defaultState,
				...state,
			});
			return stats;
		},
		{ refetchOnWindowFocus: false, retry: 0 }
	);

	const handleChartSelect = async (e) => {
		update({ chart: e.value });
	};

	const chartDataProp = state.chart || 'totalRevenue';
	return (
		<Div
			grid
			gridColumns='repeat(3, 1fr)'
			gridColumnGap='12px'
			gridRowGap='12px'
		>
			{Object.keys(DEFAULT_STATS).map((val) => (
				<Block
					key={`${val}-panel`}
					label={statPropMap[val].label}
					tooltipLabel={statPropMap[val].tooltipLabel}
					stat={formatStatValue(val, dailyOverview)}
				/>
			))}
			<Select
				onChange={handleChartSelect}
				defaultValue={{
					label: statPropMap[chartDataProp].label,
					value: chartDataProp,
				}}
				options={Object.keys(DEFAULT_STATS).map((val) => ({
					label: statPropMap[val].label,
					value: val,
				}))}
			/>

			<Chart
				gridColumn='span 3'
				isLoading={isStatsLoading}
				name={chartDataProp}
				legendInterval={interval}
				yLabel={statPropMap[chartDataProp].label}
				yFormat={statPropMap[chartDataProp].yFormat}
				xFormat={(date) =>
					getIntervalDescription(interval, { date: new Date(date) })
				}
				tooltipFormatter={(date) =>
					getIntervalDescription(interval, { date: new Date(date) })
				}
				backgroundColor={statPropMap[chartDataProp].bgColor}
				color={statPropMap[chartDataProp].color}
				data={formatStats(dailyStats?.[chartDataProp])}
			/>
		</Div>
	);
};
Stats.displayName = 'Dashboard.Stats';

function formatStats(stats = []) {
	return stats.map(({ description, date, value }) => ({
		description,
		x: date,
		y: value,
	}));
}

function getInterval(totalDays: number): IntervalOpts {
	if (totalDays > 365) {
		return 'yearly';
	} else if (totalDays > 31) {
		return 'monthly';
	} else if (totalDays > 14) {
		return 'weekly';
	} else {
		return 'daily';
	}
}

/**
 *
 * @param intervalOpt
 * @param range.date the date to format
 * @param range.toDate the very last date.  used to clamp the end of the week description
 * @returns {string}
 */
function getIntervalDescription(
	intervalOpt: IntervalOpts,
	range: { date: Date; toDate?: Date }
): string {
	switch (intervalOpt) {
		case 'yearly': {
			return format(range.date, 'yyyy');
		}
		case 'monthly': {
			return format(range.date, 'LLL yyyy');
		}
		case 'weekly': {
			const begWeek = startOfWeek(range.date);
			const endWeek = endOfWeek(range.date);
			// make sure the description doesn't include end of week if it is greater than the toDate
			const clampedEndWeek = clamp(endWeek, {
				start: range.date,
				end: range.toDate || endWeek,
			});

			return `${intlFormat(
				isBefore(begWeek, range.date) ? range.date : begWeek
			)} - ${intlFormat(clampedEndWeek)}`;
		}
		default: {
			return intlFormat(range.date);
		}
	}
}

/**
 * create bins depending on amount of stats (1 item = 1 daily stats)
 * @note this assumes everything is sorted by date
 */
function poolStats(items: Stat[], interval: IntervalOpts): Stat[] {
	const pooled = poolBy(items, interval);
	const reduced = pooled.map((pool, i) =>
		pool.reduce(
			(
				acc,
				{
					date,
					totalInfluencers,
					totalUtilizedInfluencers,
					totalConversions,
					totalInvitesClicked,
					totalRevenue,
				}
			) => ({
				date: isBefore(new Date(date), new Date(acc.date)) ? date : acc.date,
				totalConversions: acc.totalConversions + totalConversions,
				totalInfluencers: acc.totalInfluencers + totalInfluencers,
				totalInvitesClicked: acc.totalInvitesClicked + totalInvitesClicked,
				totalRevenue: acc.totalRevenue + totalRevenue,
				totalUtilizedInfluencers:
					acc.totalUtilizedInfluencers + totalUtilizedInfluencers,
				// can't total rates, so taking the average rate
				totalUtilizationRate:
					(acc.totalUtilizedInfluencers + totalUtilizedInfluencers) /
					(acc.totalInfluencers + totalInfluencers) || 0,
			}),
			{
				date: pooled[i][0].date,
				totalInfluencers: 0,
				totalUtilizedInfluencers: 0,
				totalUtilizationRate: 0,
				totalConversions: 0,
				totalInvitesClicked: 0,
				totalRevenue: 0,
			}
		)
	);

	return reduced;
}

function poolBy(stats: Stat[], poolInterval: IntervalOpts): Stat[][] {
	if (poolInterval === 'daily') {
		return stats.map((stat) => [stat]);
	}

	if (!stats.length) return [];

	const copied = [...stats];
	const start = new Date(stats[0].date);
	const end = new Date(stats[stats.length - 1].date);
	const pools = [];
	let intervalFn: (interval: Interval) => Date[] = undefined;

	switch (poolInterval) {
		case 'yearly': {
			intervalFn = eachYearOfInterval;
			break;
		}
		case 'monthly': {
			intervalFn = eachMonthOfInterval;
			break;
		}
		case 'weekly': {
			intervalFn = eachWeekOfInterval;
			break;
		}
		default: {
			break;
		}
	}

	const intervals = intervalFn({ start, end });
	for (let i = 0; i < intervals.length - 1; i++) {
		const pool = [];
		while (
			copied.length &&
			isWithinInterval(new Date(copied[0].date), {
				start: intervals[i],
				end: subDays(intervals[i + 1], 1),
			})
		) {
			pool.push(copied.shift());
		}
		pools.push(pool);
	}
	// there may be others that don't fall in the interval, so we group the remainder here
	pools.push(copied);

	return pools;
}

function formatStatValue(stat, data): string {
	if (!data) return '0';

	const value = data[stat];
	switch (stat) {
		case 'totalRevenue': {
			return value.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 0,
			});
		}
		case 'totalUtilizationRate': {
			return (value / 100).toLocaleString('en-US', {
				style: 'percent',
				minimumFractionDigits: 0,
			});
		}
		default: {
			return value.toLocaleString('en-US');
		}
	}
}

export default observer(Stats);
